/**
 * AuthStepper, three numbered dots above the headline on the
 * signup / verify / onboarding pages. Steps:
 *
 *   1. Secure the case file  (signup + verify both belong here)
 *   2. A few details         (onboarding)
 *   3. Send case file        (first /add-bill, shown as upcoming until then)
 *
 * Visual states: done / active / upcoming.
 */

import { Check } from 'lucide-react';
import '@styles/components/auth-shell.css';

type Step = {
  num: number;
  label: string;
  state: 'done' | 'active' | 'upcoming';
};

type Props = {
  /** Which point in the flow the user is on. */
  currentStep: 'signup' | 'verify' | 'onboarding';
};

export default function AuthStepper({ currentStep }: Props) {
  const steps: Step[] = [
    {
      num: 1,
      label: 'Secure',
      state:
        currentStep === 'signup' || currentStep === 'verify'
          ? 'active'
          : 'done',
    },
    {
      num: 2,
      label: 'Details',
      state:
        currentStep === 'onboarding'
          ? 'active'
          : currentStep === 'signup' || currentStep === 'verify'
            ? 'upcoming'
            : 'done',
    },
    { num: 3, label: 'Case file', state: 'upcoming' },
  ];

  return (
    <div className="auth-stepper" role="list" aria-label="Account setup progress">
      {steps.map((s, i) => (
        <div key={s.num} className="auth-stepper-item">
          <div className={`auth-stepper-step is-${s.state}`} role="listitem">
            <span className="auth-stepper-circle">
              {s.state === 'done' ? <Check size={11} strokeWidth={3} /> : s.num}
            </span>
            <span className="auth-stepper-label">{s.label}</span>
          </div>
          {i < steps.length - 1 && <span className="auth-stepper-bar" />}
        </div>
      ))}
    </div>
  );
}
